<template>
  <InternalDashboardLayout
    :roleLabel="dashboardConfig?.roleLabel || ''"
    :pageTitle="dashboardConfig?.pageTitle || ''"
    :cards="dashboardCards"
    :roleOptions="roleOptions"
    :activeRole="activeRole"
    :isLoggedIn="isLoggedIn"
    :user="user"
    :showBackButton="showBackButton"
    :onBack="goBack"
    :onLogout="handleLogout"
    :onRoleChange="handleRoleChange"
  >
    <GlobalViewModal
      v-if="canViewUsers"
      :showModal="showGlobalViewModal"
      :closeModal="closeGlobalView"
      :viewerRole="viewerRole"
    />
    <TeacherApplicationsModal
      v-if="canManageTeacherApplications"
      :showModal="showApplicationsListModal"
      :closeModal="closeApplicationsModal"
      :onViewApplication="viewApplication"
    />
    <TeacherApplicationDetailModal
      v-if="canManageTeacherApplications"
      :showModal="showApplicationDetailModal"
      :closeModal="closeApplicationDetailModal"
      :applicationId="selectedApplicationId"
      :onApplicationUpdated="handleApplicationUpdated"
      :onBack="handleBackToApplications"
    />
    <SessionTrackingModal
      :showModal="showGamesModalVisible"
      :closeModal="closeGamesModal"
      :myGamesOnly="gamesModalMode === 'my'"
      :publicGamesOnly="gamesModalMode === 'public'"
      :modalTitle="gamesModalTitle"
      :viewerRole="viewerRole"
    />
  </InternalDashboardLayout>
</template>

<script>
import { storeToRefs } from "pinia";
import InternalDashboardLayout from "@/components/internal/InternalDashboardLayout.vue";
import GlobalViewModal from "@/components/util/GlobalViewModal.vue";
import TeacherApplicationsModal from "@/components/util/TeacherApplicationsModal.vue";
import TeacherApplicationDetailModal from "@/components/util/TeacherApplicationDetailModal.vue";
import SessionTrackingModal from "@/components/util/SessionTrackingModal.vue";
import { buildDashboardCards, getDashboardConfig } from "@/config/internalDashboards";
import { useAuthStore } from "@/stores/authStore";
import { APP_ROUTE_NAMES, PERMISSIONS, USER_ROLES, getRoleRoute, getRoleSelectorOptions, userHasPermission } from "@/utils/permissions";
import gameService from "@/services/game.service";

export default {
  name: "InternalRoleDashboardView",
  components: {
    InternalDashboardLayout,
    GlobalViewModal,
    TeacherApplicationsModal,
    TeacherApplicationDetailModal,
    SessionTrackingModal,
  },
  data() {
    return {
      showGlobalViewModal: false,
      showApplicationsListModal: false,
      showApplicationDetailModal: false,
      selectedApplicationId: null,
      showGamesModalVisible: false,
      gamesModalMode: "all",
      sharingGroupsAvailable: false,
    };
  },
  computed: {
    viewerRole() {
      return this.$route.meta.dashboardRole || USER_ROLES.STUDENT;
    },
    activeRole() {
      return this.authStore.activeRole || this.viewerRole;
    },
    dashboardConfig() {
      return getDashboardConfig(this.viewerRole);
    },
    dashboardCards() {
      return buildDashboardCards(
        this.viewerRole,
        {
          showGlobalView: this.showGlobalView,
          showApplicationsModal: this.showApplicationsModal,
          showSessionsModal: this.showSessionsModal,
          showMyGames: this.showMyGames,
          showPublicGames: this.showPublicGames,
          goToGroups: this.goToGroups,
        },
        {
          sharingGroupsAvailable: this.sharingGroupsAvailable,
        },
        this.user,
        this.viewerRole
      );
    },
    roleOptions() {
      return getRoleSelectorOptions(this.user);
    },
    canViewUsers() {
      return userHasPermission(this.user, PERMISSIONS.VIEW_USERS, this.viewerRole);
    },
    canManageTeacherApplications() {
      return userHasPermission(this.user, PERMISSIONS.MANAGE_TEACHER_APPLICATIONS, this.viewerRole);
    },
    gamesModalTitle() {
      if (this.gamesModalMode === "my") return "session-modal-title-my-games";
      if (this.gamesModalMode === "public") return "session-modal-title-public-games";
      return "session-modal-title-game-tracking";
    },
    showBackButton() {
      return ![USER_ROLES.ADMIN, USER_ROLES.RESEARCHER].includes(this.viewerRole);
    },
    groupsRouteName() {
      if (this.viewerRole === USER_ROLES.RESEARCHER) return APP_ROUTE_NAMES.RESEARCHER_GROUPS;
      return APP_ROUTE_NAMES.TEACHER_GROUPS;
    },
  },
  methods: {
    showGlobalView() {
      this.showGlobalViewModal = true;
    },
    closeGlobalView() {
      this.showGlobalViewModal = false;
    },
    showApplicationsModal() {
      this.showApplicationsListModal = true;
    },
    closeApplicationsModal() {
      this.showApplicationsListModal = false;
    },
    viewApplication(applicationId) {
      this.selectedApplicationId = applicationId;
      this.showApplicationDetailModal = true;
      this.closeApplicationsModal();
    },
    closeApplicationDetailModal() {
      this.showApplicationDetailModal = false;
      this.selectedApplicationId = null;
    },
    handleApplicationUpdated() {
      this.showApplicationsListModal = true;
    },
    handleBackToApplications() {
      this.showApplicationDetailModal = false;
      this.selectedApplicationId = null;
      this.showApplicationsListModal = true;
    },
    showSessionsModal() {
      this.showGamesModal("all");
    },
    showMyGames() {
      this.showGamesModal("my");
    },
    showPublicGames() {
      this.showGamesModal("public");
    },
    showGamesModal(mode) {
      this.gamesModalMode = mode;
      this.showGamesModalVisible = true;
    },
    closeGamesModal() {
      this.showGamesModalVisible = false;
    },
    goToGroups() {
      this.$router.push({ name: this.groupsRouteName });
    },
    handleRoleChange(nextRole) {
      this.authStore.setActiveRole(nextRole);
      this.$router.push(getRoleRoute(nextRole));
    },
    handleLogout() {
      this.authStore.logout();
      this.$router.push({ name: APP_ROUTE_NAMES.HOME });
    },
    goBack() {
      this.$router.push({ name: APP_ROUTE_NAMES.HOME });
    },
  },
  async mounted() {
    if (!userHasPermission(this.user, PERMISSIONS.MANAGE_SHARING_GROUPS, this.viewerRole)) return;
    try {
      const res = await gameService.sharingGroupsAvailable();
      this.sharingGroupsAvailable = res?.available === true;
    } catch {
      this.sharingGroupsAvailable = false;
    }
  },
  setup() {
    const authStore = useAuthStore();
    const { isLoggedIn, user } = storeToRefs(authStore);

    return {
      authStore,
      isLoggedIn,
      user,
    };
  },
};
</script>
